import { asArray, isDateGreater } from './promoPrimitives';
import { getCampaign, refOfOfferInfo, toRef } from './promoModel';
import { isCampaignInNegoPeriod } from './promoPhase';
import { getOfferForecastState } from './promoForecast';

// "Afficher sur le plan promo" (#23521, spec §1 Hors-scope and §4) : one switch per offer. The states stay in the visit :
// `model.promoPlanOffers` (the legacy `appointment._promoPlanOffers`, saved with the draft, never sent to the API) maps
// `planKey` to true / false once set. An offer never set follows its campaign in `salesPlanPromo.campaigns`, the list the
// promo plan generation reads (JS:3902-3951) : every write sets all the offers of the campaign, then rebuilds that list
// from them. Port of `switchHandler` and of the automatic selection of `checkForOfferSelection` (visitStep3-promo.js:2367-2404, 3887-3900).
// An offer in forecast stays off the plan, its negotiation being only a forecast (tester feedback 2026-10-27) : no switch,
// no automatic selection, and the generation leaves it out (`forecastPlanKeys`). Its stored state applies again once the
// forecast ends.

/** A local offer by its `_key`, a national one by campaign and offer (the legacy plan generation computes the same key). */
export const planKey = (ref) => ref.offerKey || `${ref.campaignId}_${ref.campaignOfferId}`;

const isInForecast = (snapshot, ref, model = snapshot?.model) => getOfferForecastState({ ...snapshot, model }, getCampaign(snapshot, ref.campaignId), ref).active;

/** The switch is offered during the nego period only (`isCampaignInNegoPeriod`, template HTM:155), and never to an offer in forecast. */
export const isPromoPlanSwitchVisible = (snapshot, campaign, ref) => isCampaignInNegoPeriod(campaign) && !getOfferForecastState(snapshot, campaign, ref).active;

function explicitState(model, ref) {
	const value = model?.promoPlanOffers?.[planKey(ref)];
	return typeof value === 'boolean' ? value : null;
}

export const isOfferOnPromoPlan = (snapshot, ref, model = snapshot?.model) => explicitState(model, ref) ?? asArray(model?.displayOnPromoPlan).includes(ref.campaignId);

/** The offers of a campaign the switch applies to : the visible national offers, or the local offers of the visit. */
function planOffers(campaign, model) {
	if (campaign.promoLocal)
		return asArray(model?.offerInfos)
			.filter((oi) => oi.local && oi.campaignId === campaign.id)
			.map((oi) => toRef(oi.campaignId, oi.campaignOfferId, oi._key));
	return asArray(campaign.offers)
		.filter((offer) => offer.visible && offer.hasVisibleProducts)
		.map((offer) => toRef(campaign.id, offer.id));
}

/**
 * The patch of a switch change : `promoPlanOffers` gives every offer of the campaign its state (the offers never set
 * keep the one they showed), `displayOnPromoPlan` the campaigns that still have an offer on the plan.
 */
function planPatch(snapshot, campaign, model, selected) {
	const promoPlanOffers = {};
	planOffers(campaign, model).forEach((ref) => {
		const change = selected.find((s) => planKey(s.ref) === planKey(ref));
		promoPlanOffers[planKey(ref)] = change ? change.checked : isOfferOnPromoPlan(snapshot, ref, model);
	});
	const current = asArray(model?.displayOnPromoPlan);
	const listed = Object.values(promoPlanOffers).some(Boolean);
	const displayOnPromoPlan = listed ? (current.includes(campaign.id) ? current : [...current, campaign.id]) : current.filter((id) => id !== campaign.id);
	return { promoPlanOffers, displayOnPromoPlan };
}

export function setOfferOnPromoPlan(snapshot, ref, checked) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	if (!campaign || (checked && isInForecast(snapshot, ref))) return null;
	return planPatch(snapshot, campaign, snapshot.model, [{ ref, checked: !!checked }]);
}

function isNegotiated(snapshot, model, ref) {
	if (!ref.offerKey) return asArray(model?.negotiated).some((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.volumeUC > 0);
	const offerInfo = asArray(model?.offerInfos).find((oi) => oi._key === ref.offerKey);
	return isDateGreater(offerInfo?.endDate, snapshot.now, true) && asArray(offerInfo?._negociatedCampaignOffers).length > 0;
}

/**
 * An offer with negotiated volume goes on the promo plan automatically : a national one as soon as one line has UC, a
 * local one as soon as it is running and has a line, neither while in forecast. An offer switched off by the user stays
 * off. Returns the patch, or null when nothing changes. `model` is the model after the change (negotiated rows and offerInfos).
 */
export function autoSelectPromoPlan(snapshot, campaignId, model = snapshot?.model) {
	if (snapshot?.readonly && !snapshot?.isPrepa) return null;
	const campaign = getCampaign(snapshot, campaignId);
	if (!campaign) return null;
	const selected = planOffers(campaign, model)
		.filter((ref) => explicitState(model, ref) === null && !isOfferOnPromoPlan(snapshot, ref, model) && isNegotiated(snapshot, model, ref) && !isInForecast(snapshot, ref, model))
		.map((ref) => ({ ref, checked: true }));
	return selected.length ? planPatch(snapshot, campaign, model, selected) : null;
}

/** The plan keys of the negotiated offers in forecast, which the promo plan generation leaves out. */
export function forecastPlanKeys(snapshot) {
	const refs = [
		...asArray(snapshot?.model?.negotiated).map((row) => toRef(row.campaignId, row.campaignOfferId)),
		...asArray(snapshot?.model?.offerInfos)
			.filter((oi) => oi.local)
			.map(refOfOfferInfo),
	];
	return [...new Set(refs.filter((ref) => isInForecast(snapshot, ref)).map(planKey))];
}
